import {
  QueryObserverResult,
  UseMutationResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { APIClient } from 'api/APIClient';
import { AgentActions } from 'api/types/types';
import { QueryKeys } from 'constants/queryKeys';

export type TravelExpensesFormQuery = {
  id?: string;
  costType: string;
  costDate: string;
  currency: string;
  country?: string;
  city?: string;
  amount: number;
  attachments?: Attachment[];
};

export type TravelExpenseResults = {
  id: string;
  carbonEmission?: number;
  costType: string;
  costDate: string;
  currency: string;
  country: any;
  city: any;
  comment: string;
  amount: number;
  createdBy: string;
  createdDate: any;
  updatedBy: any;
  updatedDate: any;
  paymentMethod: string;
  costDateFrom: string;
  costDateTo: string;
  categoryName: string;
  categoryId: string;
  expenseReportNumber: string;
  employeePersonnelNumber: string;
  attachments: Attachment[];
};

export type Attachment = {
  company?: string;
  documentId?: string | undefined;
  expNumber?: string;
  expTransNumber?: string;
  fileContents: string;
  fileName?: string;
  fileType: string;
  name: string;
  notes?: string;
};

export type DailyAllowanceFormQuery = {
  id?: string;
  costType?: string;
  costDateFrom: string;
  costDateTo: string;
  country: string;
  city: string;
  categoryName: string;
};

export type DailyAllowanceByIdResults = {
  id: string;
  costType: string;
  costDate: string;
  currency: string;
  country: any;
  city: any;
  comment: string;
  amount: number;
  createdBy: string;
  createdDate: any;
  updatedBy: any;
  updatedDate: any;
  paymentMethod: string;
  costDateFrom: string;
  costDateTo: string;
  categoryName: string;
  categoryId: string;
  expenseReportNumber: string;
  employeePersonnelNumber: string;
  attachments: any[];
};

export type PaymentMethodsQuery = {
  categoryId: number;
  categoryDescription: string;
  label: string;
  value: string | number;
  payment: {
    paymentMethod: string;
    paymentDescription: string;
    paymentDefault: boolean;
  }[];
};

export type PerDiemQuery = {
  id: number;
  category: string;
  categoryAndQty: any;
  description: string;
};

export type AttachmentForTravelRequestResults = {
  company: string;
  documentId: string;
  fileName: string;
  expNumber: string;
  fileType: string;
  name: string;
  notes: string;
  expTransNumber: string;
  fileContents: string;
  typeId: string;
  actualCompanyId: string;
  docuRefId: string;
  data: any;
};

export type AgentActionsQuery = {
  actions?: AgentActions;
};

async function retrieveExpensesById(
  id: string | null
): Promise<TravelExpenseResults[]> {
  if (typeof id === 'undefined') {
    throw new Error('TravelId is undefined');
  }
  const { data } = await APIClient().get(`/request/${id}/cost`);
  if (data?.message) return [];
  return data;
}

export function useRetrieveExpensesById(
  id: string | null,
  manualFetchTrigger: boolean = false
): QueryObserverResult<TravelExpenseResults[], unknown> {
  return useQuery<TravelExpenseResults[], unknown>({
    queryKey: [QueryKeys.EXPENSES_BY_ID, id],
    queryFn: () => retrieveExpensesById(id),
    enabled: manualFetchTrigger,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    retry: false,
  });
}

async function retrieveDailyAllowancesById(
  id: string | null
): Promise<DailyAllowanceByIdResults[]> {
  if (typeof id === 'undefined') {
    throw new Error('TravelId is undefined');
  }
  const { data } = await APIClient().get(`/request/${id}/perDiem`);
  if (data?.message) return [];
  return data;
}

export function useRetrieveDailyAllowancesById(
  id: string | null,
  manualFetchTrigger: boolean = false
): QueryObserverResult<DailyAllowanceByIdResults[], unknown> {
  return useQuery<DailyAllowanceByIdResults[], unknown>({
    enabled: manualFetchTrigger,
    queryKey: [QueryKeys.DAILY_ALLOWANCE_BY_ID, id],
    refetchOnWindowFocus: false,
    queryFn: () => retrieveDailyAllowancesById(id),
    retry: false,
  });
}

async function getPaymentMethods(): Promise<PaymentMethodsQuery[]> {
  const { data } = await APIClient().get('/paymentMethods');
  return data;
}

export function useGetPaymentMethods(): QueryObserverResult<
  PaymentMethodsQuery[],
  unknown
> {
  return useQuery<PaymentMethodsQuery[], unknown>({
    queryKey: [QueryKeys.PAYMENT_METHODS],
    queryFn: () => getPaymentMethods(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    meta: {
      errorMessage: 'Ekki tókst að sækja kostnaðartegundir. Reyndu aftur síðar',
    },
  });
}

async function submitTravelExpensesform(
  query: TravelExpensesFormQuery,
  id: string | null
): Promise<unknown> {
  const { data } = await APIClient().post(`/request/${id}/cost`, query);
  return data;
}

export function useExpensesFormMutation(
  id: string | null
): UseMutationResult<unknown, unknown, TravelExpensesFormQuery, unknown> {
  return useMutation<unknown, unknown, TravelExpensesFormQuery, unknown>({
    mutationFn: (query: TravelExpensesFormQuery) =>
      submitTravelExpensesform(query, id),
    mutationKey: [QueryKeys.EXPENSES_FORM_QUERY, id],
    meta: {
      successMessage: 'Kostnarliður hefur verið skráður',
      errorMessage: 'Ekki tókst að skrá kostnaðarlið. Reyndu aftur síðar',
    },
  });
}

async function submitDailyAllowanceForm(
  query: DailyAllowanceFormQuery,
  id: string | null
): Promise<unknown> {
  const { data } = await APIClient().post(`/request/${id}/perDiem`, query);
  return data;
}

export function useDailyAllowanceFormMutation(
  id: string | null
): UseMutationResult<unknown, unknown, DailyAllowanceFormQuery, unknown> {
  return useMutation<unknown, unknown, DailyAllowanceFormQuery, unknown>({
    mutationFn: (query: DailyAllowanceFormQuery) =>
      submitDailyAllowanceForm(query, id),
    mutationKey: [QueryKeys.DAILY_ALLOWANCE_FORM_QUERY, id],
    meta: {
      successMessage: 'Dagpeningar hafa verið skráðir',
      errorMessage: 'Ekki tókst að skrá dagpeninga. Reyndu aftur síðar',
    },
  });
}

async function updateDailyAllowancebyId(
  query: DailyAllowanceFormQuery,
  id: string | null,
  lineId: string | null
): Promise<unknown> {
  if (!id || !lineId) {
    throw new Error('ID / LineID are undefined');
  }
  const { data } = await APIClient().patch(
    `request/${id}/perDiem/${lineId}`,
    query
  );
  return data;
}

export function useUpdateDailyAllowanceById(
  id: string | null,
  lineId: string | null
): UseMutationResult<unknown, unknown, DailyAllowanceFormQuery, unknown> {
  return useMutation<unknown, unknown, DailyAllowanceFormQuery, unknown>({
    mutationFn: (query: DailyAllowanceFormQuery) =>
      updateDailyAllowancebyId(query, id, lineId),
    mutationKey: [QueryKeys.UPDATE_DAILY_ALLOWANCE_BY_ID, id],
    meta: {
      successMessage: 'Dagpeningar hafa verið uppfærðir',
      errorMessage: 'Ekki tókst að uppfæra dagpeninga. Reyndu aftur síðar',
    },
  });
}

async function getPerDiemOptions(): Promise<PerDiemQuery[]> {
  const { data } = await APIClient().get('/perDiem');
  return data;
}

export function useGetPerDiemOptions(): QueryObserverResult<
  PerDiemQuery[],
  unknown
> {
  return useQuery<PerDiemQuery[], unknown>({
    queryKey: [QueryKeys.PER_DIEM_OPTIONS],
    queryFn: () => getPerDiemOptions(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    meta: {
      errorMessage: 'Ekki tókst að sækja dagpeningaflokka. Reyndu aftur síðar',
    },
  });
}

export type SubmitAttachmentVariables = {
  lineId: string | undefined;
  attachment: Attachment;
};

async function submitExpensesAttachment(
  id: string | null,
  lineId: string | undefined,
  attachment: Attachment
): Promise<unknown> {
  const { data } = await APIClient().post(
    `/request/${id}/cost/${lineId}/attachments`,
    attachment
  );
  return data;
}

export function useExpensesAttachment(
  id: string | null
): UseMutationResult<unknown, unknown, SubmitAttachmentVariables, unknown> {
  return useMutation<unknown, unknown, SubmitAttachmentVariables, unknown>({
    mutationFn: ({ lineId, attachment }: SubmitAttachmentVariables) =>
      submitExpensesAttachment(id, lineId, attachment),
    mutationKey: [QueryKeys.EXPENSES_ATTACHMENT],
    meta: {
      errorMessage: 'Ekki tókst að vista viðhengi. Reyndu aftur síðar',
    },
  });
}

async function getAttachmentForTravelRequest(
  id: string | null
): Promise<AttachmentForTravelRequestResults[]> {
  const { data } = await APIClient().get(`/request/${id}/attachments`);
  return data;
}

export function useGetAttachmentForTravelRequest(
  id: string | null
): QueryObserverResult<AttachmentForTravelRequestResults[], unknown> {
  return useQuery({
    queryKey: [QueryKeys.EXPENSES_ATTACHMENT_FOR_REQUEST, id],
    queryFn: () => getAttachmentForTravelRequest(id),
    enabled: false,
    meta: {
      errorMessage: 'Ekki tókst að sækja viðhengi. Reyndu aftur síðar',
    },
  });
}

async function updateExpenseById(
  query: TravelExpensesFormQuery,
  id: string | null,
  lineId: string | null
): Promise<unknown> {
  const { data } = await APIClient().patch(
    `/request/${id}/cost/${lineId}`,
    query
  );
  return data;
}

export function useUpdateExpenseById(
  id: string | null,
  lineId: string | null
): UseMutationResult<unknown, unknown, TravelExpensesFormQuery, unknown> {
  return useMutation<unknown, unknown, TravelExpensesFormQuery, unknown>({
    mutationFn: (query: TravelExpensesFormQuery) =>
      updateExpenseById(query, id, lineId),
    mutationKey: [QueryKeys.UPDATE_EXPENSE_BY_ID, id],
    meta: {
      successMessage: 'Kostnarliður hefur verið uppfærður',
      errorMessage: 'Ekki tókst að uppfæra kostnaðarlið. Reyndu aftur síðar',
    },
  });
}

async function deleteAttachment(
  id: string | null,
  lineId: string | undefined,
  documentId: string | undefined
): Promise<unknown> {
  if (!id || !lineId || !documentId) {
    throw new Error('ID / LineId / DocumentId is undefined');
  }
  const { data } = await APIClient().delete(
    `request/${id}/cost/${lineId}/attachments/${documentId}`
  );
  return data;
}

export function useDeleteAttachment(
  id: string | null,
  lineId: string | undefined,
  documentId: string | undefined
): UseMutationResult {
  return useMutation({
    mutationFn: () => deleteAttachment(id, lineId, documentId),
    mutationKey: [QueryKeys.DELETE_ATTACHMENT, documentId],
    meta: {
      successMessage: 'Viðhengi eytt.',
      errorMessage: 'Ekki tókst að eyða viðhengi. Reyndu aftur síðar',
    },
  });
}

async function deleteExpenseLine(
  id: string | null,
  lineId: string | undefined
): Promise<unknown> {
  if (!id || !lineId) {
    throw new Error('ID / LineId is undefined');
  }
  const { data } = await APIClient().delete(`request/${id}/cost/${lineId}`);
  return data;
}

export function useDeleteExpenseLine(
  id: string | null,
  lineId: string | undefined
): UseMutationResult {
  return useMutation({
    mutationFn: () => deleteExpenseLine(id, lineId),
    mutationKey: [QueryKeys.DELETE_ATTACHMENT, id],
    meta: {
      successMessage: 'Kostnaðarlið eytt.',
      errorMessage: 'Ekki tókst að eyða kostnaðarlið. Reyndu aftur síðar',
    },
  });
}

async function updateAgentActions(
  id: string | null,
  actions: AgentActions | undefined
): Promise<unknown> {
  const { data } = await APIClient().put(
    `/request/${id}/agentActions`,
    actions
  );
  return data;
}

export function useAgentActions(
  id: string | null
): UseMutationResult<unknown, unknown, AgentActions, unknown> {
  return useMutation<unknown, unknown, AgentActions, unknown>({
    mutationFn: (query: AgentActions) => updateAgentActions(id, query),
    mutationKey: [QueryKeys.UPDATE_AGENT_ACTIONS, id],
    meta: {
      successMessage: 'Aðgerð uppfærð.',
      errorMessage: 'Ekki tóskt að uppfæra aðgerð. Reyndu aftur síðar',
    },
  });
}
